import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress, Grid, IconButton, Chip, Card } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import BenefitCard from './Components/BenefitCard';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSimpleExitModal } from '../../../hooks/useSimpleExitModal';
import ExitModal from '../../../v3/Common/ExitModal';
import {  UserFileDict } from '../../../utils/settings';
import {
  setNomineeData,
  updateNomineeField,
  resetNomineeData,
} from './reducer/nomineeReducer';
import { resetBeneficiaryData } from './reducer/beneficiaryReducer';
import {
  AddBeneficiaryNomineeAction,
  getNomineeRelatioAction,
} from './APIs/action';
import ConfirmDeleteModal from './Components/DeleteModal';
import FirmDetails from './Components/FirmDetails';
import IconReusable from '../../../components/IconReusable';
import NomineeOCRForm from './Components/NomineeOCRForm';

const AddNominee = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const beneficiaryData = location?.state?.insuranceData;
  const beneficiary_details = useSelector(state => state?.beneficiaryReducer?.beneficiaryData || {});
  const errors = useSelector(state => state?.nomineeReducer?.errors || {});
  const insuranceType = location?.state?.insurance_type;
  const selectedCompanyEntity = location?.state?.selectedCompanyEntity;
  const [loader, setLoader] = useState(false);
  const [relations, setRelations] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState('AADHAAR');
  const [resetTrigger, setResetTrigger] = useState(0);
  
  // Exit modal state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showHistory = useSelector(state => state.entityReducer.show_history);
  // Simple exit modal hook
  const {
    navigate,
    forceNavigate,
    showExitModal,
    handleConfirmExit,
    handleCancelExit
  } = useSimpleExitModal(hasUnsavedChanges && !isSubmitting);
  const nominee = useSelector(state => state?.nomineeReducer?.nomineeData || {
    insurance_id: '',
    id_type: '',
    id_number: '',
    full_name: '',
    dob: '',
    age: '',
    phone_number: '',
    relation: '',
    confirm: false,
    gender: 'MALE', // Set default gender instead of empty string
  });

  // Track form changes to determine if there are unsaved changes
  useEffect(() => {
    const hasData = nominee.full_name || nominee.dob || nominee.phone_number || nominee.id_number;
    setHasUnsavedChanges(!!hasData);
  }, [nominee]);

  const getNomineeRelation = async () => {
    setLoader(true);
    const { data, error, message } = await getNomineeRelatioAction();
    setLoader(false);
    if (!error && data?.length > 0) {
      setRelations(data);
      if (!nominee.relation && data[0]?.value) {
        dispatch(updateNomineeField('relation', data[0].value));
      }
    } else {
      toast.error(message);
    }
  };
  const handleSubmit = async (e) => {
    setIsSubmitting(true); // Start submitting state
    
    const BeneficiaryNomineePayload = {
      company_esi_id: selectedCompanyEntity,
      insurance_id: insuranceType,
      firm_owner_name: beneficiary_details?.firm_owner_name || '',
      firm_type: beneficiary_details?.firm_type || '',
      beneficiary: {
        ...((beneficiary_details?.ocr_verified || beneficiary_details?.id_number) && {
          id_type: beneficiary_details?.id_type || null,
          id_number: beneficiary_details?.id_number || '',
        }),
        full_name: beneficiary_details?.full_name || '',
        gender: beneficiary_details?.gender || '',
        dob: beneficiary_details?.dob || '',
        age: beneficiary_details?.age || 0,
        phone_number: beneficiary_details?.phone_number || '',
        relation: beneficiary_details?.relation || 'SELF',
        ocr_verified: beneficiary_details?.ocr_verified || false
      },
      nominee: {
        ...(nominee?.ocr_verified && {
        id_type: nominee.id_type || '',
        id_number: nominee.id_number || '',
        }),
        full_name: nominee.full_name || '',
        gender: nominee.gender || '',
        dob: nominee.dob,
        age: nominee.age,
        phone_number: nominee.phone_number,
        relation: nominee.relation,
        ocr_verified: nominee.ocr_verified ?? false // Explicitly set false if undefined
      }
    };

    const { error, message } = await AddBeneficiaryNomineeAction(BeneficiaryNomineePayload);
    if (!error) {
      setHasUnsavedChanges(false); // Clear unsaved changes on successful submit
      toast.success(message);
      forceNavigate('/beneficiary/detail', {
        state: {
          insurance_type: insuranceType,
          selectedCompanyEntity: selectedCompanyEntity
        },
      });
    } else {
      toast.error(message);
    }
    
    setIsSubmitting(false); // End submitting state
  };
  
  useEffect(() => {
    if (insuranceType && !nominee.insurance_id) {
      dispatch(setNomineeData({
        insurance_id: insuranceType,
        id_type: selectedDocumentType === 'PAN' ? UserFileDict.PAN_CARD : UserFileDict.AADHAR_CARD,
      }));
    }
  }, [insuranceType, nominee.insurance_id, selectedDocumentType, dispatch]);
  useEffect(() => {
    if (nominee.relation?.toLowerCase() === 'self') {
      setSelectedDocumentType('PAN');
    } else if (nominee.relation && nominee.relation?.toLowerCase() !== 'self') {
      setSelectedDocumentType('AADHAAR');
    }
  }, [nominee.relation]);

  useEffect(() => {
    if ( insuranceType && selectedCompanyEntity) {
      dispatch({ type: 'RESET_NOMINEE_DATA' });
      getNomineeRelation()
    } else {
      navigate('/beneficiary');
    }
  }, [insuranceType, selectedCompanyEntity]);

  const handleDocumentTypeChange = (documentType) => {
    setSelectedDocumentType(documentType);

    // Reset OCR-related fields when document type changes
    dispatch(setNomineeData({
      ...nominee,
      id_type: documentType === 'PAN' ? UserFileDict.PAN_CARD : UserFileDict.AADHAR_CARD,
      id_number: '',
      full_name: '',
      dob: '',
      age: '',
      gender: 'MALE', // Ensure gender has default value when resetting
      confirm: false
    }));

    // Increment resetTrigger to reset OCR component
    setResetTrigger(prev => prev + 1);
  };
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handleDeleteBeneficiaryClick = () => {
    // Reset both reducers
    setLoading(true);
    dispatch(resetBeneficiaryData());
    dispatch(resetNomineeData());
    setLoading(false);

    // Navigate to add beneficiary page
    navigate('/beneficiary/add-beneficiary', {
      state: {
        insurance_type: insuranceType,
        selectedCompanyEntity: selectedCompanyEntity
      },
    });
  };
  

  const {
    benefit_card,
    selected_firm,
    confirmation_text,
  } = beneficiaryData;

  if (!beneficiaryData) {
    return (
      <CircularProgress
        size={24}
        sx={{ color: '#FFA525' }}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 2 }}>
      <Box sx={{ mb: 5 }}>
        {!loader ? (
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <Grid
              container
              spacing={2}
              sx={{ alignItems: 'stretch', mb: 3 }}
            >
              <Grid
                item
                xs={12}
                sm={6}
              >
                <BenefitCard
                  item={benefit_card}
                  sx={{ height: '100%' }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              >
            
                <Card sx={{
                  boxShadow: 'none',
                  p: 2, 
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-evenly',
               }}>
                  {selected_firm &&
                    selected_firm.map((detail, i) => (
                      <React.Fragment key={i}>
                        <FirmDetails detail={detail} />
                      </React.Fragment>
                    ))}
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <CircularProgress
              size={24}
              sx={{ color: '#FFA525' }}
            />
          </Box>
        )}
        
          {!loader && (
            <Grid
              container
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Grid
                item
                xs={12}
                sm={6}
              >
                <Box sx={{  background: '#fff', gap: 2, borderRadius: 2 , p: 2 }}>
                  <Box sx={{ display: 'flex',flexDirection: 'column', gap: '16px', borderRadius: 2, background: '#fff' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px'}}>
                      <Typography variant='h2' >
                        Beneficiary Details
                      </Typography>
                      <IconButton
                        size='small'
                        sx={{ color: !showHistory ? '#995A00' : '#E5E5E5' }}
                        disabled={showHistory}
                        onClick={()=>setShowDeleteModal(true)}
                      >
                        <IconReusable name='Delete' color={!showHistory ? '#995A00' : '#E5E5E5' }/>
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex',flexDirection: 'column', gap: '16px'}}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px'}}>
                        <Typography variant='labelD' sx={{ color:'#666666'}}>Name</Typography>
                        <Typography variant='p3' sx={{ textAlign: 'right'}}>{beneficiary_details.full_name}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px'}}>
                        <Typography variant='labelD'  sx={{ color:'#666666'}}>D.O.B</Typography>
                        <Typography variant='p3' sx={{ textAlign: 'right'}}>{beneficiary_details.dob}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px'}}>
                        <Typography variant='labelD'  sx={{ color:'#666666'}}>Gender</Typography>
                        <Typography variant='p3' sx={{ textAlign: 'right'}}>{beneficiary_details.gender}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px'}}>
                        <Typography variant='labelD'  sx={{ color:'#666666'}}>Age</Typography>
                        <Typography variant='p3' sx={{ textAlign: 'right'}}>{beneficiary_details.age}y</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px'}}>
                        <Typography variant='labelD'  sx={{ color:'#666666'}}>Relation</Typography>
                        <Typography variant='p3' sx={{ textAlign: 'right'}}>{beneficiary_details.relation}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px'}}>
                        <Typography variant='labelD'  sx={{ color:'#666666'}}>Mobile No.</Typography>
                        <Typography variant='p3' sx={{ textAlign: 'right'}}>{beneficiary_details.phone_number}</Typography>
                      </Box>
                    </Box>
                  </Box>
                 </Box>
              </Grid>
            </Grid>
          )}
          <Box
            // sx={{ border: '1px solid #D9D9D9', p: 2, borderRadius: 3, mb: 2 }}
          >
            <NomineeOCRForm
              nominee={nominee}
              onFieldChange={(field, value) => {
                if (typeof field === 'object') {
                  dispatch(setNomineeData({ ...nominee, ...field }));
                } else {
                  dispatch(updateNomineeField(field, value));
                }
              }}
              relations={relations}
              errors={errors}
              onSubmit={handleSubmit}
              confirmation_text={confirmation_text}
              selectedDocumentType={selectedDocumentType}
              resetTrigger={resetTrigger}
              onDocumentTypeChange={handleDocumentTypeChange}
            />
          </Box>
      </Box>
      <ConfirmDeleteModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteBeneficiaryClick}
        loading={loading}
        isNominee={false}
      />
      
      {/* Exit Modal */}
      <ExitModal
        open={showExitModal}
        title="Are you sure you want to exit?"
        subtitle="This action cannot be undone. All data would be lost?"
        confirmLabel="Yes"
        cancelLabel="No"
        onClose={handleCancelExit}
        onConfirm={handleConfirmExit}
        loading={false}
      />
    </Box>
  );
};

export default AddNominee;
